import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Group,
  IconArrowLeft,
  IconEdit,
  IconEye,
  Loader,
  Textblock,
  Token,
  Tooltip,
  Typography,
  Wrapper
} from '@screentone/core';
import classnames from 'classnames';
import { cloneDeep } from 'lodash';

import { Preview } from 'components/preview/Preview';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { SectionContainer } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { usePublicationSettings } from 'hooks/publication-settings';
import { AuraEmployeeResponse, useAuraQuery } from 'hooks/useAuraQuery';
import { formatInTimeZone } from 'utils/dates';
import { TIMES } from 'utils/time';
import styles from './HistoryItem.module.scss';
import { HistoryContent } from './types';

interface HistoryItemProps<T extends HistoryContent> {
  selectedVersion: T;
  isLive: boolean;
  isPast: boolean;
  isEditing: boolean;
  handleShowHistoryEditMode?: (historyItem: HistoryContent) => void;
  scrollableSectionClassName?: string;
  handlePreviewClick?: (selectedVersion: T) => void;
  isPreviewLoading?: boolean;
  isWindowLoading?: boolean;
  // showURL: boolean;
  isReadOnly?: boolean;
  hidePreview?: boolean;
}

export const HistoryItem = <T extends HistoryContent>({
  selectedVersion,
  isLive,
  isPast,
  isEditing,
  handleShowHistoryEditMode,
  scrollableSectionClassName,
  handlePreviewClick,
  isPreviewLoading,
  isWindowLoading,
  isReadOnly,
  hidePreview
}: HistoryItemProps<T>) => {
  const { setNewRoot, renderEntity } = useDataModelContext<SectionContainer>();
  const currentProperty = useConvertedProperty();
  const { data: publicationSettingsResp } = usePublicationSettings();
  const { data: revisorUserData } = useAuraQuery(
    selectedVersion.metadata.revisorUser?.djUserId ?? '',
    { currentProperty }
  );
  const revisorUserDataTyped = revisorUserData as AuraEmployeeResponse | undefined;
  const revisorUserText = revisorUserDataTyped
    ? `${revisorUserDataTyped.first_name ?? ''} ${revisorUserDataTyped.last_name ?? ''}`
    : selectedVersion.metadata.revisorUser?.djUserId;
  const { publishUtc } = selectedVersion.metadata;

  const now = new Date().getTime();
  const headerText = isLive ? 'Published' : isPast ? 'Previous' : 'Scheduled';
  const publishDateText = publishUtc
    ? formatInTimeZone(
        publishUtc,
        publicationSettingsResp?.publicationSetting.preferredTimezone,
        'MMM d, yyyy h:mm aaa',
        { showTimezoneAbbreviation: true }
      )
    : '';
  const [justCopiedToClipboard, setJustCopiedToClipboard] = useState(false);
  // const [versionURL, setVersionURL] = useState('');

  const isCorrupted = !(selectedVersion as { root?: unknown }).root;
  const bodyError = isCorrupted
    ? (selectedVersion as { metadata?: { bodyError?: string } }).metadata?.bodyError
    : undefined;
  useEffect(() => {
    if (justCopiedToClipboard) {
      setTimeout(() => setJustCopiedToClipboard(false), TIMES.ONE_SECOND);
    }
  }, [justCopiedToClipboard]);

  const handleCopyToNew = () => {
    setNewRoot(selectedVersion.root);
  };

  // const copyVersionURLToClipboard = async () => {
  //   const {
  //     location: { origin, pathname }
  //   } = window;
  //   // Take the path of the current URL up to the revision group key or slug.
  //   const path = pathname.slice(1).split('/').slice(0, 3).join('/');

  //   const url = `${origin}/${path}/version/${publishUtc!}`;
  //   await navigator.clipboard.writeText(url);
  //   setVersionURL(url);
  //   setJustCopiedToClipboard(true);
  // };

  const handleShowEditMode = () => handleShowHistoryEditMode?.(selectedVersion);

  const isEditingVersion = isEditing && !isLive;

  const historySection = isCorrupted ? null : renderEntity(cloneDeep(selectedVersion.root), { hierarchyId: '', isHistory: true });

  return (
    <Box>
      <Wrapper padding={{ all: 'md' }}>
        <Wrapper className={styles.publishState}>
          <Typography variant="header3" margin={{ right: 'sm' }} data-testid="page-published-title">
            {headerText}
          </Typography>
          {isLive && (
            <Token color="emerald" margin={{ right: 'md' }} data-testid="page-published-live-label">
              Live
            </Token>
          )}
          <Typography variant="note" data-testid="page-published-date-label">
            {publishDateText}
          </Typography>
        </Wrapper>
        <Wrapper className={styles.actions}>
          <Tooltip>
            {justCopiedToClipboard && (
              <Tooltip.Content data-testid="page-copy-clipboard-alert">Copied to clipboard</Tooltip.Content>
            )}
            {/* {showURL && (
              <Tooltip.Trigger>
                <Button
                  tertiary
                  disabled={isEditingVersion}
                  margin={{ right: 'md' }}
                  icon={IconLink}
                  onClick={copyVersionURLToClipboard}
                  data-testid="page-published-url-button"
                  data-version-url={versionURL}
                >
                  URL
                </Button>
              </Tooltip.Trigger>
            )} */}
          </Tooltip>

          {!isCorrupted && (
            <Tooltip>
              <Tooltip.Trigger>
                <Button
                  tertiary
                  disabled={isEditingVersion || isReadOnly}
                  onClick={handleCopyToNew}
                  margin={{ right: 'sm' }}
                  icon={IconArrowLeft}
                  data-testid="page-published-copy-new-button"
                >
                  Replace draft
                </Button>
                {!hidePreview && (handlePreviewClick ? (
                  <Button
                    tertiary
                    margin={{ right: 'md' }}
                    icon={IconEye}
                    onClick={() => handlePreviewClick(selectedVersion)}
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                    disabled={isPreviewLoading || isEditingVersion}
                    data-testid="page-published-preview-button"
                  >
                    Preview
                  </Button>
                ) : (
                   <Preview selectedVersion={selectedVersion} />
                ))}
              </Tooltip.Trigger>
            </Tooltip>
          )}

          {/* only show if we're not in edit mode and if the page will publish >= 1 minute from now */}
          {!isEditingVersion &&
            selectedVersion.metadata.publishUtc &&
            selectedVersion.metadata.publishUtc > now + TIMES.ONE_MINUTE && (
              <Button
                tertiary
                onClick={handleShowEditMode}
                icon={IconEdit}
                data-testid="page-scheduled-edit-button"
                disabled={isReadOnly}
              >
                Edit
              </Button>
            )}
        </Wrapper>
      </Wrapper>
      <Divider />
      <Wrapper
        className={classnames(scrollableSectionClassName, {
          [styles.historyScrollable]: isEditingVersion || isWindowLoading
        })}
        data-testid="page-published-content"
      >
        {isCorrupted ? (
          <Textblock margin={{ all: 'md' }}>
            <p>This version could not be displayed.</p>
            {bodyError && <code>{bodyError}</code>}
          </Textblock>
        ) : (
          <>
            {historySection}
            {isEditingVersion && (
              <Group
                align="center"
                valign="center"
                gap="sm"
                fullWidth
                className={styles.editNotice}
                data-testid="page-editing-status-label"
              >
                <IconArrowLeft />
                <Typography data-testid="page-editing-status">In edit</Typography>
              </Group>
            )}
            {isWindowLoading && (
              <Group
                align="center"
                valign="start"
                fullWidth
                className={styles.editNotice}
                data-testid="page-published-content-loader"
              >
                <Loader size="xl" margin={{ top: 'md' }} />
              </Group>
            )}
          </>
        )}
      </Wrapper>
      {revisorUserText && (
        <>
          <Divider />
          <Wrapper padding={{ all: 'md' }}>
            <Typography variant="note" className={styles.lastEditedByText} data-testid="page-last-editor-label">
              Last edited by: {revisorUserText}
            </Typography>
          </Wrapper>
        </>
      )}
    </Box>
  );
};
